import type { ButtonDesignTokens } from '@primeuix/themes/aura/button';

export default {
  root: {
    borderRadius: '{form.field.border.radius}',
    roundedBorderRadius: '2rem',
    gap: '0.5rem',
    paddingX: '{form.field.padding.x}',
    paddingY: '{form.field.padding.y}',
    iconOnlyWidth: '2.5rem',
    sm: {
      fontSize: '0.875rem',
      paddingX: '0.625rem',
      paddingY: '0.375rem',
      iconOnlyWidth: '2rem',
    },
    lg: {
      fontSize: '1.125rem',
      paddingX: '0.875rem',
      paddingY: '0.625rem',
      iconOnlyWidth: '3rem',
    },
    label: {
      fontWeight: '500',
    },
    raisedShadow: '0 3px 1px -2px rgba(0, 0, 0, 0.2),0 2px 2px 0 rgba(0, 0, 0, 0.14),0 1px 5px 0 rgba(0, 0, 0, 0.12)',
    focusRing: {
      width: '{focus.ring.width}',
      style: '{focus.ring.style}',
      offset: '{focus.ring.offset}',
    },
    badgeSize: '1rem',
    transitionDuration: '{form.field.transition.duration}',
  },
  colorScheme: {
    light: {
      root: {
        primary: {
          background: '{primary.color}',
          hoverBackground: '{primary.hover.color}',
          activeBackground: '{primary.active.color}',
          borderColor: '{primary.color}',
          hoverBorderColor: '{primary.hover.color}',
          activeBorderColor: '{primary.active.color}',
          color: '{primary.contrast.color}',
          hoverColor: '{primary.contrast.color}',
          activeColor: '{primary.contrast.color}',
          focusRing: {
            color: '{primary.color}',
            shadow: '0 0 0 0 #00000000',
          },
        },
        secondary: {
          background: '{surface.100}',
          hoverBackground: '{surface.200}',
          activeBackground: '{surface.300}',
          borderColor: '{surface.100}',
          hoverBorderColor: '{surface.200}',
          activeBorderColor: '{surface.300}',
          color: '{surface.600}',
          hoverColor: '{surface.700}',
          activeColor: '{surface.800}',
          focusRing: {
            color: '{surface.600}',
            shadow: '0 0 0 0 #00000000',
          },
        },
        info: {
          background: '{blue.500}',
          hoverBackground: '{blue.600}',
          activeBackground: '{blue.700}',
          borderColor: '{blue.500}',
          hoverBorderColor: '{blue.600}',
          activeBorderColor: '{blue.700}',
          color: '#ffffff',
          hoverColor: '#ffffff',
          activeColor: '#ffffff',
          focusRing: {
            color: '{blue.500}',
            shadow: '0 0 0 0 #00000000',
          },
        },
        success: {
          background: '{emerald.500}',
          hoverBackground: '{emerald.600}',
          activeBackground: '{emerald.700}',
          borderColor: '{emerald.500}',
          hoverBorderColor: '{emerald.600}',
          activeBorderColor: '{emerald.700}',
          color: '#ffffff',
          hoverColor: '#ffffff',
          activeColor: '#ffffff',
          focusRing: {
            color: '{emerald.500}',
            shadow: '0 0 0 0 #00000000',
          },
        },
        warn: {
          background: '{yellow.500}',
          hoverBackground: '{yellow.600}',
          activeBackground: '{yellow.700}',
          borderColor: '{yellow.500}',
          hoverBorderColor: '{yellow.600}',
          activeBorderColor: '{yellow.700}',
          color: '#ffffff',
          hoverColor: '#ffffff',
          activeColor: '#ffffff',
          focusRing: {
            color: '{yellow.500}',
            shadow: '0 0 0 0 #00000000',
          },
        },
        help: {
          background: '{cyan.500}',
          hoverBackground: '{cyan.600}',
          activeBackground: '{cyan.700}',
          borderColor: '{cyan.500}',
          hoverBorderColor: '{cyan.600}',
          activeBorderColor: '{cyan.700}',
          color: '#ffffff',
          hoverColor: '#ffffff',
          activeColor: '#ffffff',
          focusRing: {
            color: '{cyan.500}',
            shadow: '0 0 0 0 #00000000',
          },
        },
        danger: {
          background: '{red.500}',
          hoverBackground: '{red.600}',
          activeBackground: '{red.700}',
          borderColor: '{red.500}',
          hoverBorderColor: '{red.600}',
          activeBorderColor: '{red.700}',
          color: '#ffffff',
          hoverColor: '#ffffff',
          activeColor: '#ffffff',
          focusRing: {
            color: '{red.500}',
            shadow: '0 0 0 0 #00000000',
          },
        },
        contrast: {
          background: '{surface.950}',
          hoverBackground: '{surface.900}',
          activeBackground: '{surface.800}',
          borderColor: '{surface.950}',
          hoverBorderColor: '{surface.900}',
          activeBorderColor: '{surface.800}',
          color: '{surface.0}',
          hoverColor: '{surface.0}',
          activeColor: '{surface.0}',
          focusRing: {
            color: '{surface.950}',
            shadow: '0 0 0 0 #00000000',
          },
        },
      },
      outlined: {
        primary: {
          hoverBackground: '{primary.50}',
          activeBackground: '{primary.100}',
          borderColor: '{primary.200}',
          color: '{primary.color}',
        },
        secondary: {
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
          borderColor: '{surface.200}',
          color: '{surface.500}',
        },
        success: {
          hoverBackground: '{emerald.50}',
          activeBackground: '{emerald.100}',
          borderColor: '{emerald.200}',
          color: '{emerald.500}',
        },
        info: {
          hoverBackground: '{blue.50}',
          activeBackground: '{blue.100}',
          borderColor: '{blue.200}',
          color: '{blue.500}',
        },
        warn: {
          hoverBackground: '{yellow.50}',
          activeBackground: '{yellow.100}',
          borderColor: '{yellow.200}',
          color: '{yellow.500}',
        },
        help: {
          hoverBackground: '{cyan.50}',
          activeBackground: '{cyan.100}',
          borderColor: '{cyan.200}',
          color: '{cyan.500}',
        },
        danger: {
          hoverBackground: '{red.50}',
          activeBackground: '{red.100}',
          borderColor: '{red.200}',
          color: '{red.500}',
        },
        contrast: {
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
          borderColor: '{surface.700}',
          color: '{surface.950}',
        },
        plain: {
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
          borderColor: '{surface.200}',
          color: '{surface.700}',
        },
      },
      text: {
        primary: {
          hoverBackground: '{primary.50}',
          activeBackground: '{primary.100}',
          color: '{primary.color}',
        },
        secondary: {
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
          color: '{surface.500}',
        },
        success: {
          hoverBackground: '{emerald.50}',
          activeBackground: '{emerald.100}',
          color: '{emerald.500}',
        },
        info: {
          hoverBackground: '{blue.50}',
          activeBackground: '{blue.100}',
          color: '{blue.500}',
        },
        warn: {
          hoverBackground: '{yellow.50}',
          activeBackground: '{yellow.100}',
          color: '{yellow.500}',
        },
        help: {
          hoverBackground: '{cyan.50}',
          activeBackground: '{cyan.100}',
          color: '{cyan.500}',
        },
        danger: {
          hoverBackground: '{red.50}',
          activeBackground: '{red.100}',
          color: '{red.500}',
        },
        contrast: {
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
          color: '{surface.950}',
        },
        plain: {
          hoverBackground: '{surface.50}',
          activeBackground: '{surface.100}',
          color: '{surface.700}',
        },
      },
      link: {
        color: '{primary.color}',
        hoverColor: '{primary.color}',
        activeColor: '{primary.color}',
      },
    },
    dark: {
      root: {
        primary: {
          background: '{primary.color}',
          hoverBackground: '{primary.hover.color}',
          activeBackground: '{primary.active.color}',
          borderColor: '{primary.color}',
          hoverBorderColor: '{primary.hover.color}',
          activeBorderColor: '{primary.active.color}',
          color: '{primary.contrast.color}',
          hoverColor: '{primary.contrast.color}',
          activeColor: '{primary.contrast.color}',
          focusRing: {
            color: '{primary.color}',
            shadow: '0 0 0 0 #00000000',
          },
        },
        secondary: {
          background: '{surface.800}',
          hoverBackground: '{surface.700}',
          activeBackground: '{surface.600}',
          borderColor: '{surface.800}',
          hoverBorderColor: '{surface.700}',
          activeBorderColor: '{surface.600}',
          color: '{surface.300}',
          hoverColor: '{surface.200}',
          activeColor: '{surface.100}',
          focusRing: {
            color: '{surface.300}',
            shadow: '0 0 0 0 #00000000',
          },
        },
        info: {
          background: '{sky.400}',
          hoverBackground: '{sky.300}',
          activeBackground: '{sky.200}',
          borderColor: '{sky.400}',
          hoverBorderColor: '{sky.300}',
          activeBorderColor: '{sky.200}',
          color: '{sky.950}',
          hoverColor: '{sky.950}',
          activeColor: '{sky.950}',
          focusRing: {
            color: '{sky.400}',
            shadow: '0 0 0 0 #00000000',
          },
        },
        success: {
          background: '{green.400}',
          hoverBackground: '{green.300}',
          activeBackground: '{green.200}',
          borderColor: '{green.400}',
          hoverBorderColor: '{green.300}',
          activeBorderColor: '{green.200}',
          color: '{green.950}',
          hoverColor: '{green.950}',
          activeColor: '{green.950}',
          focusRing: {
            color: '{green.400}',
            shadow: '0 0 0 0 #00000000',
          },
        },
        warn: {
          background: '{orange.400}',
          hoverBackground: '{orange.300}',
          activeBackground: '{orange.200}',
          borderColor: '{orange.400}',
          hoverBorderColor: '{orange.300}',
          activeBorderColor: '{orange.200}',
          color: '{orange.950}',
          hoverColor: '{orange.950}',
          activeColor: '{orange.950}',
          focusRing: {
            color: '{orange.400}',
            shadow: '0 0 0 0 #00000000',
          },
        },
        help: {
          background: '{cyan.400}',
          hoverBackground: '{cyan.300}',
          activeBackground: '{cyan.200}',
          borderColor: '{cyan.400}',
          hoverBorderColor: '{cyan.300}',
          activeBorderColor: '{cyan.200}',
          color: '{cyan.950}',
          hoverColor: '{cyan.950}',
          activeColor: '{cyan.950}',
          focusRing: {
            color: '{cyan.400}',
            shadow: '0 0 0 0 #00000000',
          },
        },
        danger: {
          background: '{red.400}',
          hoverBackground: '{red.300}',
          activeBackground: '{red.200}',
          borderColor: '{red.400}',
          hoverBorderColor: '{red.300}',
          activeBorderColor: '{red.200}',
          color: '{red.950}',
          hoverColor: '{red.950}',
          activeColor: '{red.950}',
          focusRing: {
            color: '{red.400}',
            shadow: '0 0 0 0 #00000000',
          },
        },
        contrast: {
          background: '{surface.0}',
          hoverBackground: '{surface.100}',
          activeBackground: '{surface.200}',
          borderColor: '{surface.0}',
          hoverBorderColor: '{surface.100}',
          activeBorderColor: '{surface.200}',
          color: '{surface.950}',
          hoverColor: '{surface.950}',
          activeColor: '{surface.950}',
          focusRing: {
            color: '{surface.0}',
            shadow: '0 0 0 0 #00000000',
          },
        },
      },
      outlined: {
        primary: {
          hoverBackground: 'color-mix(in srgb, {primary.color}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {primary.color}, transparent 84%)',
          borderColor: '{primary.700}',
          color: '{primary.color}',
        },
        secondary: {
          hoverBackground: 'rgba(255, 255, 255, 0.04)',
          activeBackground: 'rgba(255, 255, 255, 0.16)',
          borderColor: '{surface.700}',
          color: '{surface.400}',
        },
        success: {
          hoverBackground: 'color-mix(in srgb, {green.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {green.400}, transparent 84%)',
          borderColor: '{green.700}',
          color: '{green.400}',
        },
        info: {
          hoverBackground: 'color-mix(in srgb, {sky.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {sky.400}, transparent 84%)',
          borderColor: '{sky.700}',
          color: '{sky.400}',
        },
        warn: {
          hoverBackground: 'color-mix(in srgb, {orange.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {orange.400}, transparent 84%)',
          borderColor: '{orange.700}',
          color: '{orange.400}',
        },
        help: {
          hoverBackground: 'color-mix(in srgb, {cyan.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {cyan.400}, transparent 84%)',
          borderColor: '{cyan.700}',
          color: '{cyan.400}',
        },
        danger: {
          hoverBackground: 'color-mix(in srgb, {red.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {red.400}, transparent 84%)',
          borderColor: '{red.700}',
          color: '{red.400}',
        },
        contrast: {
          hoverBackground: '{surface.800}',
          activeBackground: '{surface.700}',
          borderColor: '{surface.500}',
          color: '{surface.0}',
        },
        plain: {
          hoverBackground: '{surface.800}',
          activeBackground: '{surface.700}',
          borderColor: '{surface.600}',
          color: '{surface.0}',
        },
      },
      text: {
        primary: {
          hoverBackground: 'color-mix(in srgb, {primary.color}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {primary.color}, transparent 84%)',
          color: '{primary.color}',
        },
        secondary: {
          hoverBackground: '{surface.800}',
          activeBackground: '{surface.700}',
          color: '{surface.400}',
        },
        success: {
          hoverBackground: 'color-mix(in srgb, {green.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {green.400}, transparent 84%)',
          color: '{green.400}',
        },
        info: {
          hoverBackground: 'color-mix(in srgb, {sky.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {sky.400}, transparent 84%)',
          color: '{sky.400}',
        },
        warn: {
          hoverBackground: 'color-mix(in srgb, {orange.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {orange.400}, transparent 84%)',
          color: '{orange.400}',
        },
        help: {
          hoverBackground: 'color-mix(in srgb, {cyan.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {cyan.400}, transparent 84%)',
          color: '{cyan.400}',
        },
        danger: {
          hoverBackground: 'color-mix(in srgb, {red.400}, transparent 96%)',
          activeBackground: 'color-mix(in srgb, {red.400}, transparent 84%)',
          color: '{red.400}',
        },
        contrast: {
          hoverBackground: '{surface.800}',
          activeBackground: '{surface.700}',
          color: '{surface.0}',
        },
        plain: {
          hoverBackground: '{surface.800}',
          activeBackground: '{surface.700}',
          color: '{surface.0}',
        },
      },
      link: {
        color: '{primary.color}',
        hoverColor: '{primary.color}',
        activeColor: '{primary.color}',
      },
    },
  },
} satisfies ButtonDesignTokens;
